// Reine Berechnungsfunktionen (kein reaktiver Zustand) - analog zu useSkalierung.js.
// Findet moegliche Duplikate im Zutaten-Katalog auf zwei Arten:
// 1) Namens-Aehnlichkeit (Levenshtein-Distanz) - faengt Tippfehler/Schreibvarianten ab
//    (z.B. "Bananen"/"Bananaen"), versagt aber bei sehr unterschiedlich langen Namen.
// 2) Teilstring-Enthaltensein - faengt Faelle wie "Öl"/"Rapsöl"/"Sonnenblumenöl" ab,
//    bei denen der kuerzere Name komplett im laengeren steckt (deutsche Komposita
//    werden ohne Leerzeichen geschrieben, daher reicht reine Wort-Tokenisierung nicht).

const SCHWELLENWERT = 0.72
const MIN_LAENGE = 3
const MIN_LAENGE_TEILSTRING = 2

function Levenshtein(a, b) {
  const m = a.length
  const n = b.length
  if (m === 0) return n
  if (n === 0) return m

  const Zeile = new Array(n + 1)
  for (let j = 0; j <= n; j++) Zeile[j] = j

  for (let i = 1; i <= m; i++) {
    let Vorherige = Zeile[0]
    Zeile[0] = i
    for (let j = 1; j <= n; j++) {
      const Temp = Zeile[j]
      Zeile[j] = a[i - 1] === b[j - 1]
        ? Vorherige
        : 1 + Math.min(Vorherige, Zeile[j], Zeile[j - 1])
      Vorherige = Temp
    }
  }
  return Zeile[n]
}

// 1 = identisch, 0 = komplett verschieden
export function Aehnlichkeit(NameA, NameB) {
  const A = NameA.toLowerCase().trim()
  const B = NameB.toLowerCase().trim()
  if (A === B) return 1
  const MaxLaenge = Math.max(A.length, B.length)
  if (MaxLaenge === 0) return 1
  return 1 - Levenshtein(A, B) / MaxLaenge
}

// true, wenn der kuerzere Name (als Ganzes) im laengeren enthalten ist, z.B. "Öl" in "Rapsöl".
function EnthaeltAlsTeilstring(NameA, NameB) {
  const A = NameA.toLowerCase().trim()
  const B = NameB.toLowerCase().trim()
  if (A === B) return false
  if (A.length < MIN_LAENGE_TEILSTRING || B.length < MIN_LAENGE_TEILSTRING) return false
  return A.includes(B) || B.includes(A)
}

// Gruppiert Zutaten ueber Union-Find: gelten zwei Namen ueber die Aehnlichkeit ODER das
// Teilstring-Kriterium als verwandt, landen sie (transitiv) in derselben Gruppe.
export function DuplikatGruppenFinden(Zutaten) {
  const Eltern = new Map(Zutaten.map(z => [z.Id, z.Id]))

  function Wurzel(Id) {
    let Wurz = Id
    while (Eltern.get(Wurz) !== Wurz) Wurz = Eltern.get(Wurz)
    while (Eltern.get(Id) !== Wurz) {
      const Naechster = Eltern.get(Id)
      Eltern.set(Id, Wurz)
      Id = Naechster
    }
    return Wurz
  }

  for (let i = 0; i < Zutaten.length; i++) {
    const NameA = Zutaten[i].Name.trim()
    for (let j = i + 1; j < Zutaten.length; j++) {
      const NameB = Zutaten[j].Name.trim()
      const AehnlichGenug = NameA.length >= MIN_LAENGE && NameB.length >= MIN_LAENGE
        && Aehnlichkeit(NameA, NameB) >= SCHWELLENWERT
      const Verwandt = AehnlichGenug || EnthaeltAlsTeilstring(NameA, NameB)
      if (Verwandt) {
        const WurzelA = Wurzel(Zutaten[i].Id)
        const WurzelB = Wurzel(Zutaten[j].Id)
        if (WurzelA !== WurzelB) Eltern.set(WurzelA, WurzelB)
      }
    }
  }

  const Gruppen = new Map()
  for (const Zutat of Zutaten) {
    const Wurz = Wurzel(Zutat.Id)
    if (!Gruppen.has(Wurz)) Gruppen.set(Wurz, [])
    Gruppen.get(Wurz).push(Zutat)
  }

  return Array.from(Gruppen.values())
    .filter(Mitglieder => Mitglieder.length > 1)
    .map(Mitglieder => {
      let BesteScore = 0
      let HatAehnlichkeitTreffer = false
      for (let i = 0; i < Mitglieder.length; i++) {
        for (let j = i + 1; j < Mitglieder.length; j++) {
          const Score = Aehnlichkeit(Mitglieder[i].Name, Mitglieder[j].Name)
          if (Score > BesteScore) BesteScore = Score
          if (Score >= SCHWELLENWERT) HatAehnlichkeitTreffer = true
        }
      }
      return {
        Mitglieder: Mitglieder.slice().sort((a, b) => a.Name.localeCompare(b.Name)),
        Score: BesteScore,
        // Gruppe existiert nur wegen Teilstring-Enthaltensein, nicht wegen echter Namens-Aehnlichkeit -
        // ein Prozentwert waere hier irrefuehrend (z.B. "Öl"/"Sonnenblumenöl" hat kaum Aehnlichkeit).
        NurTeilstring: !HatAehnlichkeitTreffer
      }
    })
    .sort((a, b) => b.Score - a.Score)
}
